import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';
import { useGetOrganizationQuery } from '../../api/organizations.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import { getInitials } from '../../utils/getInitials.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import {
    PERMISSION_CONTACTS,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS
} from '../../utils/constants.js';
import OrganizationNews from '../../components/misc/organizations/OrganizationNews.jsx';
import IconArrowRight from '../../components/images/IconArrowRight.jsx';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';
import IconKPIs from '../../components/images/IconKPIs.jsx';
import OrganizationContacts from '../../components/misc/organizations/OrganizationContacts.jsx';
import OrganizationProjects from '../../components/misc/organizations/OrganizationProjects.jsx';

const removeDashes = (text) => {
    return text ? text.replace(/-/g, ' ') : '';
};

const OrganizationDetail = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const { id } = useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useGetOrganizationQuery(id);

    const organization = data?.response?.organization;

    const canEdit = (() => {
        if (organization) {
            if (
                ['fully-owned', 'partially-owned'].includes(organization.ownership) &&
                userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.update
            ) {
                return true;
            }
            if (
                organization.ownership === 'partner-company' &&
                userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.update
            ) {
                return true;
            }
        }

        return false;
    })();

    const displayOrder = [
        'domain',
        'headquarters',
        'founded',
        'employees',
        'revenue',
        'profit',
        'expenses',
        'assets',
        'liabilities',
        'stock_symbol'
    ];

    const filteredProperties = Object.entries(organization || {})
        .filter(([key, value]) => !!value && displayOrder.includes(key))
        .sort(([keyA], [keyB]) => displayOrder.indexOf(keyA) - displayOrder.indexOf(keyB));

    if (isLoading) {
        return (
            <AuthLayout title='Organization Details' backRoute='/organizations/list'>
                <ThreeDotsLoader className='mt-6' />
            </AuthLayout>
        );
    }

    if (!data) {
        return (
            <AuthLayout title='Organization Details' backRoute='/organizations/list'>
                <div className='text-center mt-6'>Organization not found.</div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title='Organization Details' backRoute='/organizations/list'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <BaseCard className='p-4 bg-theme1-white'>
                    <div className='flex items-center gap-4'>
                        <div className='w-16 h-16 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-2xl flex-shrink-0'>
                            {getInitials(organization?.name, 'organization')}
                        </div>
                        <div>
                            <h4 className='font-extrabold text-[22px] text-theme1-primary'>{organization?.name}</h4>
                            {organization?.industry && (
                                <div className='text-sm text-theme1-text2 capitalize font-bold'>
                                    {organization.industry}
                                </div>
                            )}
                            <div className='text-sm text-theme1-text2 capitalize font-bold mt-[2px]'>
                                {removeDashes(organization.ownership)}
                            </div>
                        </div>
                        {organization.stock_symbol_id && (
                            <div
                                className='ml-auto cursor-pointer'
                                onClick={() => navigate(`/organizations/kpis/${id}`)}
                            >
                                <div className='rounded-2xl w-20 h-20 overflow-hidden'>
                                    <div className='w-20 h-20 flex items-center justify-center'>
                                        <div className='w-full h-full text-theme1-primary font-bold text-xl'>
                                            <div className='w-full h-full bg-theme1-gold p-5 rounded-2xl flex items-center justify-center'>
                                                <IconKPIs className='w-7 h-7 stroke-theme1-white' />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </BaseCard>

                {filteredProperties.length > 0 && (
                    <div className='mt-6 bg-theme1-white p-4 rounded-xl shadow'>
                        <ul className='divide-y divide-theme1-text3'>
                            {filteredProperties.map(([key, value]) => (
                                <li key={key} className='flex justify-between py-2'>
                                    <span className='font-bold capitalize text-theme1-text1'>
                                        {key.replace('_', ' ')}
                                    </span>
                                    <span className='text-theme1-text2'>
                                        {['revenue', 'profit', 'expenses'].includes(key)
                                            ? formatNumbers(Number(value), '$')
                                            : ['headquarters'].includes(key)
                                              ? value?.code && value?.name
                                              : value}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <OrganizationProjects orgId={id} orgName={organization?.name} />

                <OrganizationContacts orgId={id} />

                <div className={`${organization?.news_enabled ? 'mb-6' : 'mb-10'} border p-4 rounded-2xl mt-6`}>
                    {organization?.news_enabled ? (
                        <>
                            <div className='text-lg font-bold text-theme1-primary mb-2'>News enabled</div>
                            <div className='flex gap-2 mb-2'>
                                <div className='text-theme1-text1 font-bold'>Frequency:</div>
                                <div>
                                    {organization?.news_frequency?.charAt(0).toUpperCase() +
                                        organization?.news_frequency?.slice(1)}
                                </div>
                            </div>
                            <div className='flex gap-2'>
                                <div className='text-theme1-text1 font-bold'>Keywords:</div>
                                <div className='flex flex-wrap'>
                                    {organization?.news_keywords.map((keyword, index, arr) => (
                                        <span key={keyword} className='mr-2'>
                                            {keyword}
                                            {index < arr.length - 1 ? ', ' : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className='text-theme1-primary'>News are not enabled for this organization</div>
                    )}
                </div>

                <OrganizationNews orgId={id} newsEnabled={organization?.news_enabled} />

                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-0'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Reach &amp; Presence</div>
                        {canEdit && (
                            <div onClick={() => navigate(`/organizations/edit/${id}`)}>
                                <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                            </div>
                        )}
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${(!organization?.international && !organization?.reach?.length) || !organization?.reach?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Reach</span>
                                            <Badge
                                                value={
                                                    organization?.international
                                                        ? 'International'
                                                        : organization?.reach?.length
                                                }
                                                className='ns-badge'
                                            ></Badge>
                                        </>
                                    }
                                    disabled={
                                        (!organization?.international && !organization?.reach?.length) ||
                                        !organization?.reach?.length
                                    }
                                >
                                    {!organization?.international && organization?.reach?.length > 0 && (
                                        <div className='flex items-center flex-wrap'>
                                            {organization?.reach &&
                                                organization?.reach.map((region) => (
                                                    <span
                                                        key={region.id}
                                                        className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                    >
                                                        {region.name}
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className={`custom-accordion-tab-no-padding ${!organization?.presence?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Offices</span>
                                            <Badge value={organization?.presence?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!organization?.presence?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {organization?.presence &&
                                            organization?.presence.map((presence) => (
                                                <span
                                                    key={presence.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {presence.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>

                {!!userPermissions[PERMISSION_CONTACTS]?.read && organization?.files?.length > 0 && (
                    <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 max-w-3xl w-full mx-auto'>
                        <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                            <div className='text-2xl font-bold text-theme1-title2'>Files</div>
                        </div>
                        <BaseCard className='border-0 m-0 py-0 px-4'>
                            <div className='max-w-3xl w-full mx-auto px-4 pb-4'>
                                {organization.files.map((file) => {
                                    return (
                                        <div key={file.id} className='w-full flex pt-4 items-center'>
                                            <a className='flex-grow text-sm text-theme1-text1' href={file.url}>
                                                {file.title}
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        </BaseCard>
                    </BaseCard>
                )}

                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-0'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Needs &amp; Capabilities</div>
                        {canEdit && (
                            <div onClick={() => navigate(`/organizations/edit/${id}`)}>
                                <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                            </div>
                        )}
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${!organization?.needs?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                            <Badge value={organization?.needs?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!organization?.needs?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {organization?.needs &&
                                            organization?.needs.map((need) => (
                                                <span
                                                    key={need.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {need.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className={`custom-accordion-tab-no-padding ${!organization?.capabilities?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Capabilities</span>
                                            <Badge
                                                value={organization?.capabilities?.length}
                                                className='ns-badge'
                                            ></Badge>
                                        </>
                                    }
                                    disabled={!organization?.capabilities?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {organization?.capabilities &&
                                            organization?.capabilities.map((capability) => (
                                                <span
                                                    key={capability.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {capability.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>

                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-0'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Aggregated Needs &amp; Capabilities</div>
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${!organization?.aggregated_needs?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                            <Badge
                                                value={organization?.aggregated_needs?.length}
                                                className='ns-badge'
                                            ></Badge>
                                        </>
                                    }
                                    disabled={!organization?.aggregated_needs?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {organization?.aggregated_needs &&
                                            organization?.aggregated_needs.map((need) => (
                                                <span
                                                    key={need.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {need.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className={`custom-accordion-tab-no-padding ${!organization?.aggregated_capabilities?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Capabilities</span>
                                            <Badge
                                                value={organization?.aggregated_capabilities?.length}
                                                className='ns-badge'
                                            ></Badge>
                                        </>
                                    }
                                    disabled={!organization?.aggregated_capabilities?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {organization?.aggregated_capabilities &&
                                            organization?.aggregated_capabilities.map((capability) => (
                                                <span
                                                    key={capability.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {capability.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>

                {canEdit && (
                    <BaseButton className='btn-primary mt-10' onClick={() => navigate(`/organizations/edit/${id}`)}>
                        Edit
                    </BaseButton>
                )}
            </div>
        </AuthLayout>
    );
};

export default OrganizationDetail;
